import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Chip,
  Checkbox,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Stack,
  Skeleton,
  Autocomplete,
  TextField,
  useTheme,
  alpha,
} from '@mui/material';
import SearchableSelect from '../../components/common/SearchableSelect';
import {
  Insights,
  Compare,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  School as SchoolIcon,
  CheckBox,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { useSessionContext } from '../../context/SessionContext';
import { useAppSelector } from '../../store/hooks';
import { useDebounce } from '../../hooks/useDebounce';
import { getGradeColorFromPercentage } from '../../utils/gradeUtils';
import OutcomeChip from '../../components/common/OutcomeChip';
import DownloadMenu from '../../components/common/DownloadMenu';
import GradesheetDocument from '../../components/examination/pdf/GradesheetDocument';
import ExamComparisonDocument from '../../components/examination/pdf/ExamComparisonDocument';
import { usePdfExport, useSchoolBranding } from '../../utils/pdf';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import { examService } from '../../services/examService';
import { Session, SessionClass, SectionInstance } from '../../types/sessions';
import studentReportsService, {
  ExamGradesheet,
  ExamComparison,
} from '../../services/studentReportsService';

/**
 * Exam Reports — two modes:
 *   1. Single Exam gradesheet — rows sorted by overall % desc, subject-wise
 *      marks, total, grade. Download PDF or CSV.
 *   2. Compare Exams — pick 2–8 published exams in the same session+class,
 *      view side-by-side percentages, average, and delta. Same download menu.
 *
 * Permission: `student_reports.read` on the backend; school/system admins
 * bypass automatically. Sidebar item hides for users without the scope.
 *
 * UI conventions match ExpenseApprovalPage: header on left, download menu
 * on top-right (single button, dropdown with PDF/CSV), no zebra striping,
 * elegant hover only. Percentage + grade colours come from the school's own
 * grade scheme via getGradeColorFromPercentage (same helper used in
 * StudentReports / EnterMarks) so the palette stays consistent everywhere.
 */

type ReportMode = 'gradesheet' | 'comparison';

/** A name as it can sit in a file name — "Mid Term" → "Mid_Term". */
const fileSlug = (s: string) => s.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);

interface ExamOption {
  _id:       string;
  name:      string;
  startDate: string;
  endDate:   string;
  status:    'draft' | 'published';
}

const ExamReportsPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { activeSession } = useSessionContext();
  const grades = useAppSelector((state) => state.grades.grades);

  // ── Filters (shared across modes) ─────────────────────────────────────
  const [mode, setMode] = useState<ReportMode>('gradesheet');

  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes,  setClasses]  = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [exams,    setExams]    = useState<ExamOption[]>([]);

  const [selectedSession, setSelectedSession] = useState('');
  const [selectedClass,   setSelectedClass]   = useState('');
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [selectedExam,    setSelectedExam]    = useState<string>('');
  const [selectedExams,   setSelectedExams]   = useState<string[]>([]);

  const [gradesheet, setGradesheet] = useState<ExamGradesheet | null>(null);
  const [comparison, setComparison] = useState<ExamComparison | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  // Download menu (mirrors ExpenseApprovalPage)

  // Default to active session on mount.
  useEffect(() => {
    if (activeSession?._id && !selectedSession) setSelectedSession(activeSession._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession]);

  useEffect(() => {
    (async () => {
      try {
        const [ss, cs] = await Promise.all([sessionsApi.list(), classesApi.list()]);
        setSessions(ss);
        setClasses(cs);
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Failed to load filters', { variant: 'error' });
      }
    })();
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (!selectedSession || !selectedClass) { setSections([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const secs = await sectionInstancesApi.list({
          sessionId: selectedSession,
          classId:   selectedClass,
        });
        if (!cancelled) setSections(secs);
      } catch {/* silent */}
    })();
    return () => { cancelled = true; };
  }, [selectedSession, selectedClass]);

  useEffect(() => {
    if (!selectedSession || !selectedClass) { setExams([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await examService.getExams({
          sessionId: selectedSession,
          classId:   selectedClass,
          status:    'published',
          limit:     100,
        });
        if (cancelled) return;
        const rows = (res.data?.exams || []).map((e: any) => ({
          _id:       e._id,
          name:      e.name,
          startDate: e.startDate,
          endDate:   e.endDate,
          status:    e.status,
        }));
        setExams(rows);
      } catch {/* silent — empty list means user needs to publish an exam */}
    })();
    return () => { cancelled = true; };
  }, [selectedSession, selectedClass]);

  // Reset report + downstream selection when scope changes.
  useEffect(() => {
    setGradesheet(null);
    setComparison(null);
    setError(null);
    setSelectedExam('');
    setSelectedExams([]);
  }, [selectedSession, selectedClass, selectedSection, mode]);

  // ── Actions ────────────────────────────────────────────────────────────

  const generateGradesheet = useCallback(async () => {
    if (!selectedExam) return;
    setLoading(true); setError(null);
    try {
      const data = await studentReportsService.getGradesheet({
        examId:            selectedExam,
        sectionInstanceId: selectedSection || null,
      });
      setGradesheet(data);
      setComparison(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to generate report';
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedExam, selectedSection, enqueueSnackbar]);

  const generateComparison = useCallback(async () => {
    if (selectedExams.length < 2) return;
    setLoading(true); setError(null);
    try {
      const data = await studentReportsService.getComparison({
        examIds:           selectedExams,
        sectionInstanceId: selectedSection || null,
      });
      setComparison(data);
      setGradesheet(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to generate comparison';
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedExams, selectedSection, enqueueSnackbar]);

  // ── Auto-fetch (gradesheet only) ───────────────────────────────────────
  // Gradesheet: single exam → one selection → auto-refresh is a clear win.
  // Compare: user picks 2–8 exams via a multi-select checklist — auto-fetch
  // would feel jumpy while they're still choosing, so that mode keeps its
  // explicit Compare button below.
  const debouncedExam = useDebounce(selectedExam, 250);

  useEffect(() => {
    if (mode !== 'gradesheet') return;
    if (!selectedSession || !selectedClass || !debouncedExam) return;
    generateGradesheet();
    // generateGradesheet already tracks the latest inputs via useCallback deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedSession, selectedClass, selectedSection, debouncedExam]);

  // The PDF is drawn here from the report on screen, in the school's grade colours; the CSV comes from the server.
  const { generate: generatePdf } = usePdfExport();
  const { branding } = useSchoolBranding();
  const handleExport = useCallback(async (fmt: 'pdf' | 'csv') => {
    const scope = (r: { class: { name: string }; section: { name: string } | null }) =>
      `${fileSlug(r.class.name)}${r.section ? `_${fileSlug(r.section.name)}` : ''}`;
    const generatedAt = new Date().toISOString();
    if (mode === 'gradesheet' && gradesheet) {
      if (fmt === 'csv') return studentReportsService.downloadGradesheetCsv({ examId: gradesheet.exam._id, sectionInstanceId: gradesheet.section?._id ?? null });
      await generatePdf(
        <GradesheetDocument data={gradesheet} grades={grades} branding={branding} generatedAt={generatedAt} />,
        `Gradesheet_${fileSlug(gradesheet.exam.name)}_${scope(gradesheet)}.pdf`,
      );
    } else if (mode === 'comparison' && comparison) {
      if (fmt === 'csv') return studentReportsService.downloadComparisonCsv({ examIds: comparison.exams.map(e => e.examId), sectionInstanceId: comparison.section?._id ?? null });
      await generatePdf(
        <ExamComparisonDocument data={comparison} grades={grades} branding={branding} generatedAt={generatedAt} />,
        `Comparison_${scope(comparison)}.pdf`,
      );
    }
  }, [mode, gradesheet, comparison, grades, branding, generatePdf]);

  // ── Derived ────────────────────────────────────────────────────────────

  const examOptionsSorted = useMemo(
    () => [...exams].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()),
    [exams]
  );

  const hasResult = mode === 'gradesheet' ? Boolean(gradesheet) : Boolean(comparison);

  return (
    <Box>
      {/* ── Header (matches ExpenseApprovalPage) ────────────────────── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Exam Reports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Class-wide gradesheets ranked by percentage, and cross-exam comparisons.
          </Typography>
        </Box>

        <DownloadMenu heading="Export current view" disabled={!hasResult} onExport={handleExport} />
      </Box>

      {/* ── Mode tabs ────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <Tabs
          value={mode}
          onChange={(_, v) => setMode(v)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ px: 1 }}
        >
          <Tab
            value="gradesheet"
            sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Insights fontSize="small" />
                <span>Single Exam Gradesheet</span>
              </Stack>
            }
          />
          <Tab
            value="comparison"
            sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Compare fontSize="small" />
                <span>Compare Exams</span>
              </Stack>
            }
          />
        </Tabs>
      </Paper>

      {/* ── Filter panel (Stack layout, mirrors FeeInvoicesPage) ─────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          flexWrap="wrap"
          useFlexGap
        >
          <SearchableSelect
            label="Session"
            value={selectedSession}
            onChange={setSelectedSession}
            options={sessions.map(s => ({
              value: s._id,
              label: s.name,
              badge: s.status === 'active'
                ? <Chip label="Active" size="small" color="success" sx={{ height: 18, fontSize: 10 }} />
                : undefined,
            }))}
            placeholder="Session"
            clearable={false}
            minWidth={180}
          />

          <SearchableSelect
            label="Class"
            value={selectedClass}
            onChange={setSelectedClass}
            options={classes.map(c => ({ value: c._id, label: c.name }))}
            placeholder="Class"
            disabled={!selectedSession}
            clearable={false}
            minWidth={140}
          />

          <SearchableSelect
            label="Section"
            value={selectedSection}
            onChange={setSelectedSection}
            options={sections.map(s => ({ value: s._id, label: s.name }))}
            placeholder="Whole class"
            disabled={!selectedClass || sections.length === 0}
            minWidth={160}
          />

          {mode === 'gradesheet' ? (
            <SearchableSelect
              label="Exam"
              value={selectedExam}
              onChange={setSelectedExam}
              options={examOptionsSorted.map(e => ({
                value: e._id,
                label: e.name,
                secondary: format(new Date(e.startDate), 'dd MMM yyyy'),
              }))}
              placeholder="Pick an exam"
              disabled={!selectedClass || examOptionsSorted.length === 0}
              clearable={false}
              minWidth={260}
            />
          ) : (
            <Autocomplete
              multiple
              size="small"
              // Keep the dropdown open on select so the admin can tick multiple
              // exams in one go without reopening. Same idiom used across MUI
              // multi-select filters.
              disableCloseOnSelect
              disabled={!selectedClass || examOptionsSorted.length === 0}
              options={examOptionsSorted.map(e => e._id)}
              value={selectedExams}
              onChange={(_, v) => setSelectedExams(v.slice(0, 8))}
              getOptionLabel={(id) => {
                const e = examOptionsSorted.find(x => x._id === id);
                return e ? e.name : id;
              }}
              // Compact option rows — checkbox on the left, exam name on
              // one line, date as small caption underneath. Matches the
              // density of SearchableSelect options used by the other
              // filters instead of the bulky single-line label.
              renderOption={(props, id, { selected }) => {
                const e = examOptionsSorted.find(x => x._id === id);
                const { key, ...liProps } = props as any;
                return (
                  <li key={key} {...liProps} style={{ paddingTop: 4, paddingBottom: 4 }}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBox fontSize="small" />}
                      size="small"
                      style={{ marginRight: 8, padding: 4 }}
                      checked={selected}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }} noWrap>
                        {e?.name || id}
                      </Typography>
                      {e?.startDate && (
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                          {format(new Date(e.startDate), 'dd MMM yyyy')}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((id, index) => {
                  const e = examOptionsSorted.find(x => x._id === id);
                  const { key, ...tagProps } = getTagProps({ index }) as any;
                  return (
                    <Chip key={key} label={e?.name || id} size="small" {...tagProps} />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Exams (2–8)"
                  placeholder={selectedExams.length ? '' : 'Pick exams to compare'}
                />
              )}
              slotProps={{
                paper: { sx: { minWidth: 280 } },
              }}
              sx={{ flex: 1, minWidth: 260 }}
            />
          )}

          {/* Gradesheet mode auto-refreshes; only compare mode gets an
              explicit trigger button (user needs to lock in a multi-select
              before firing the request). Inline spinner shows fetch state. */}
          {mode === 'gradesheet' && loading && (
            <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center', color: 'text.secondary', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Loading…</Typography>
            </Box>
          )}
          {mode === 'comparison' && (
            <Box sx={{ ml: { md: 'auto' }, width: { xs: '100%', md: 'auto' } }}>
              <Button
                fullWidth
                variant="contained"
                disableElevation
                disabled={!selectedSession || !selectedClass || selectedExams.length < 2 || loading}
                onClick={generateComparison}
                sx={{ textTransform: 'none', fontWeight: 600, minWidth: 140, height: 40 }}
              >
                {loading
                  ? <CircularProgress size={18} sx={{ color: 'inherit' }} />
                  : 'Compare'}
              </Button>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* ── Empty / error / loading states ───────────────────────────── */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Skeleton variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={30} sx={{ mb: 1, borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={30} sx={{ mb: 1, borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={30} sx={{ borderRadius: 1 }} />
        </Paper>
      )}

      {!loading && !hasResult && !error && (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
          <SchoolIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            {mode === 'gradesheet'
              ? 'Pick a session, class, and exam — the gradesheet loads automatically.'
              : 'Pick 2–8 published exams in the same class to compare.'}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            Ranked by percentage (highest first). Absent students appear as AB; missing marks as —.
          </Typography>
        </Paper>
      )}

      {!loading && gradesheet && (
        <GradesheetView data={gradesheet} grades={grades} theme={theme} />
      )}

      {!loading && comparison && (
        <ComparisonView data={comparison} grades={grades} theme={theme} />
      )}
    </Box>
  );
};

// ── Gradesheet view ────────────────────────────────────────────────────────

const GradesheetView: React.FC<{
  data: ExamGradesheet;
  grades: any[];
  theme: any;
}> = ({ data, grades, theme }) => {
  // Solid header background — semi-transparent alpha bleeds through when the
  // table has `stickyHeader` (scrolled rows show through the header cells).
  // Mirrors the pattern used by AttendancePage.tsx:1249.
  const headerBg = theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f8f9fa';

  // When every student has the same totalMax (the usual case), lift "/max"
  // into the header once. Row cells then show just the obtained number,
  // matching the subject-column pattern. When maxes differ per student
  // (section-specific curriculum), keep the max in each cell for honesty.
  const uniformMax =
    data.students.length > 0 &&
    data.students.every(s => s.totalMax === data.students[0].totalMax);
  const uniformMaxValue = uniformMax ? data.students[0].totalMax : null;

  const distribution = data.stats.gradeDistribution;

  return (
    <Box>
      {/* Summary strip — class average pill on the left, grade distribution
          chips on the right (mirrors the marks-report summary pattern). */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
              Class Average
            </Typography>
            <Chip
              label={`${data.stats.classAverage.toFixed(1)}%`}
              size="small"
              sx={{
                background: getGradeColorFromPercentage(data.stats.classAverage, grades as any),
                color: '#fff',
                fontWeight: 700,
                minWidth: 64,
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {data.stats.passRules && (
              <Chip
                size="small"
                variant="outlined"
                label={`Passed ${data.stats.passed} · Failed ${data.stats.failed}`}
                sx={{ fontWeight: 700 }}
              />
            )}
            {distribution.map(b => (
              <Chip
                key={b.grade}
                size="small"
                label={
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                    <Box component="span" sx={{ fontWeight: 700 }}>{b.grade}</Box>
                    <Box
                      component="span"
                      sx={{
                        background: alpha('#ffffff', 0.25),
                        color: '#fff',
                        borderRadius: 999,
                        px: 0.75,
                        py: 0.1,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        minWidth: 20,
                        textAlign: 'center',
                      }}
                    >
                      {b.count}
                    </Box>
                  </Box>
                }
                sx={{
                  background: b.count > 0 ? b.color : alpha(theme.palette.action.disabled, 0.15),
                  color: b.count > 0 ? '#fff' : theme.palette.text.disabled,
                  opacity: b.count > 0 ? 1 : 0.6,
                  fontWeight: 600,
                  height: 26,
                }}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{data.exam.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {data.class.name}{data.section ? ` · ${data.section.name}` : ''}  ·  {data.session.name}
          </Typography>
        </Box>

        <TableContainer sx={{ maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>Rank</TableCell>
                <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>Roll</TableCell>
                <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>Student</TableCell>
                {!data.section && (
                  <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>Section</TableCell>
                )}
                {data.subjects.map(s => (
                  <TableCell key={s.subjectId} align="center" sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Box>{s.name}</Box>
                    <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 500 }}>/{s.maxMarks}</Typography>
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Box>Total</Box>
                  {uniformMaxValue != null && uniformMaxValue > 0 && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 500 }}>
                      /{uniformMaxValue}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>%</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>Grade</TableCell>
                {data.stats.passRules && (
                  <TableCell align="center" sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>Result</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.students.map((s) => {
                const pctColor = getGradeColorFromPercentage(s.overallPct, grades);
                return (
                  <TableRow
                    key={s.studentId}
                    hover
                    sx={{
                      '&:hover': { background: alpha(theme.palette.primary.main, 0.03) },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>{s.rank ?? '—'}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{s.rollNumber}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{s.name}</TableCell>
                    {!data.section && (
                      <TableCell sx={{ color: 'text.secondary' }}>{s.sectionName || '—'}</TableCell>
                    )}
                    {s.perSubject.map(c => (
                      <TableCell
                        key={c.subjectId}
                        align="center"
                        sx={{
                          // A failed paper (pass rules on) reads red.
                          color: s.failedSubjectIds.includes(c.subjectId)
                            ? theme.palette.error.main
                            : c.status === 'PRESENT'
                            ? 'text.primary'
                            : c.status === 'ABSENT'
                              ? theme.palette.error.main
                              : c.status === 'PENDING'
                                ? theme.palette.warning.main
                                : 'text.disabled',
                          fontWeight: c.status === 'PRESENT' ? 500 : 600,
                        }}
                      >
                        {/* Subject max already shown in header — display just
                           the obtained marks (or the AB/— sentinel). */}
                        {c.status === 'PRESENT' && c.obtainedRaw != null
                          ? c.obtainedRaw
                          : c.display}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {s.totalMax <= 0
                        ? '—'
                        : uniformMax
                          ? s.totalObtained
                          : `${s.totalObtained}/${s.totalMax}`}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={`${s.overallPct.toFixed(1)}%`}
                        sx={{
                          background: pctColor,
                          color: '#fff',
                          fontWeight: 700,
                          minWidth: 60,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: pctColor }}>
                      {s.overallGrade || '—'}
                    </TableCell>
                    {data.stats.passRules && (
                      <TableCell align="center">
                        <OutcomeChip
                          outcome={s.outcome}
                          failedSubjects={data.subjects.filter(sub => s.failedSubjectIds.includes(sub.subjectId)).map(sub => sub.name)}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {data.students.length === 0 && (
                <TableRow>
                  <TableCell colSpan={99} align="center" sx={{ py: 5, color: 'text.disabled' }}>
                    No enrolled students in this scope.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

// ── Comparison view ────────────────────────────────────────────────────────

const ComparisonView: React.FC<{
  data: ExamComparison;
  grades: any[];
  theme: any;
}> = ({ data, grades, theme }) => {
  // Solid header background — semi-transparent alpha bleeds through when the
  // table has `stickyHeader` (scrolled rows show through the header cells).
  // Mirrors the pattern used by AttendancePage.tsx:1249.
  const headerBg = theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f8f9fa';
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {data.class.name}{data.section ? ` · ${data.section.name}` : ''}  —  {data.exams.length} exams compared
        </Typography>
        <Typography variant="caption" color="text.secondary">{data.session.name}</Typography>
      </Box>

      <TableContainer sx={{ maxHeight: 640 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>Roll</TableCell>
              <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>Student</TableCell>
              {!data.section && (
                <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>Section</TableCell>
              )}
              {data.exams.map(e => (
                <TableCell key={e.examId} align="center" sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Box>{e.name}</Box>
                  <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 500 }}>
                    {format(new Date(e.startDate), 'dd MMM')}
                  </Typography>
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>Average</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>Δ (First → Last)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.students.map((s, i) => {
              const avgColor = getGradeColorFromPercentage(s.averagePct, grades);
              return (
                <TableRow key={s.studentId} hover sx={{ '&:hover': { background: alpha(theme.palette.primary.main, 0.03) } }}>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>{i + 1}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{s.rollNumber}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{s.name}</TableCell>
                  {!data.section && (
                    <TableCell sx={{ color: 'text.secondary' }}>{s.sectionName || '—'}</TableCell>
                  )}
                  {s.perExam.map(p => (
                    <TableCell key={p.examId} align="center">
                      {p.overallPct == null ? (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      ) : (
                        <Chip
                          size="small"
                          label={`${p.overallPct.toFixed(1)}%`}
                          sx={{
                            background: getGradeColorFromPercentage(p.overallPct, grades),
                            color: '#fff',
                            fontWeight: 700,
                            minWidth: 60,
                          }}
                        />
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 700, color: avgColor }}>
                    {s.averagePct.toFixed(1)}%
                  </TableCell>
                  <TableCell align="center">
                    {s.deltaPct == null ? (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    ) : s.trend === 'up' ? (
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ color: theme.palette.success.main, fontWeight: 700 }}>
                        <TrendingUp fontSize="small" />
                        <span>+{s.deltaPct.toFixed(1)}</span>
                      </Stack>
                    ) : s.trend === 'down' ? (
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ color: theme.palette.error.main, fontWeight: 700 }}>
                        <TrendingDown fontSize="small" />
                        <span>{s.deltaPct.toFixed(1)}</span>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ color: 'text.secondary' }}>
                        <TrendingFlat fontSize="small" />
                        <span>0.0</span>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {data.students.length === 0 && (
              <TableRow>
                <TableCell colSpan={99} align="center" sx={{ py: 5, color: 'text.disabled' }}>
                  No enrolled students in this scope.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// ── Small building blocks ──────────────────────────────────────────────────

const StatTile: React.FC<{ label: string; value: string; accent?: boolean }> = ({ label, value, accent }) => (
  <Grid size={{ xs: 6, sm: 3 }}>
    <Paper variant="outlined" sx={{ p: 1.75, textAlign: 'center', borderRadius: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: accent ? 'primary.main' : 'text.primary' }}>
        {value}
      </Typography>
    </Paper>
  </Grid>
);

export default ExamReportsPage;
